import axios from "axios";
import { isArray, isEmpty, map, omitBy, isNil, first } from "lodash";
import normalize from "Utils/normalizer";
import useSession from "Hooks/session.hook";
import errorHandler from "Utils/error-handler";
import { getAccessToken } from "Helpers/session";

/**
 * This is not placed in order-setting.services.ts because the
 * apis were created by another developer, and there is no such
 * thing as consistency from BE I guess...
 *
 * So the normalisers will also be different
 */

const url = `${process.env.API_URL}/${process.env.API_VER}`;

interface Pagination {
  offset: number;
  limit: number;
}

/**
 * This creates the customer rate table.
 * @param data
 * @returns
 */
export const createCustomerRateTableSetting = async (data: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer_rate_table/create`,
      data: normalize.model.customer_rate_table(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createCustomerRateTableSetting(data);
      const { value, code, message, stack, info } = await errorHandler.response(error, {
        callback,
      });
      return { error: { value, code, message, stack, info } };
    } else {
      console.log(err.message);
      return { error: { message: err.message, info: err?.info } };
    }
  }
};

/**
 * Update rate table
 * @param data
 * @returns
 */
export const updateCustomerRateTableSetting = async (data: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer_rate_table/update`,
      data: normalize.model.customer_rate_table(data),
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateCustomerRateTableSetting(data);
      const { value, code, message, stack, info } = await errorHandler.response(error, {
        callback,
      });
      return { error: { value, code, message, stack, info } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

/**
 * Fetch customer rate table list
 * @param filters
 * @param pagination
 * @returns
 */
export const fetchCustomerRateTableList = async (
  filters: any,
  pagination?: Pagination,
  sorting?: any,
) => {
  const accessToken = getAccessToken();

  try {
    const response: any = await axios({
      method: "POST",
      url: `${url}/customer_rate_table/list`,
      data: omitBy(
        {
          filter: omitBy(
            {
              orgID: filters?.orgID,
              customerOrgID: filters?.customerOrgID,
              includeInactive: filters?.includeInactive,
              orFields: filters?.orFields,
            },
            isNil,
          ),
          pagination,
          sorting,
        },
        (val) => (typeof val === "object" ? isEmpty(val) : isNil(val)),
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error
      ? response.data.customerRateTables.active.map((a) =>
          normalize.response.order_setting(a, response.data.customerRateTables.inactive),
        )
      : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    console.log(error);
    if (!isEmpty(error)) {
      const callback = async () => await fetchCustomerRateTableList(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchCustomerRateTable = async (orderSettingID: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer_rate_table/fetch`,
      data: { customerRateTableID: orderSettingID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.customer_rate_table(response.data.customerRateTable);
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchCustomerRateTable(orderSettingID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchEstimatedDeliveryDays = async (customerID: string, postCode: string) => {
  const accessToken = getAccessToken();
  const payload = {
    filter: {
      orgID: customerID,
      postalCode: postCode
    }
  }

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/customer_rate_table/get_estimated_delivery_day/list`,
      data: payload,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.customerRateTableData[0].estimatedDeliveryInDay;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchEstimatedDeliveryDays(customerID, postCode);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
}

/**
 * Fetch customer rate table list
 * @param filters
 * @param pagination
 * @returns
 */
export const fetchUnassignedCustomerList = async (
  filters: any,
  pagination?: Pagination,
  sorting?: any,
) => {
  const accessToken = getAccessToken();

  try {
    const response: any = await axios({
      method: "POST",
      url: `${url}/customer_rate_table/customer/org/list`,
      data: omitBy(
        {
          filter: omitBy(
            {
              orgID: filters?.orgID,
              listAll: filters?.listAll,
            },
            isNil,
          ),
          pagination,
          sorting,
        },
        (val) => (typeof val === "object" ? isEmpty(val) : isNil(val)),
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return !response.error ? response.data.customers : response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    console.log(error);
    if (!isEmpty(error)) {
      const callback = async () => await fetchCustomerRateTableList(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};
